/** /otas/:id — OTA editor. Loads the OTA and renders the shared OTAForm seeded
 *  for edit; Save PATCHes the full record (domains sent as a full-array upsert).
 *  Delete is gated on manage + confirmed. Read-only when the user lacks manage. */
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Tab, Tabs } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as otasApi from '@/api/otas';
import type { OTAWrite } from '@/api/otas';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EditorSaveBar } from '@/components/EditorSaveBar/EditorSaveBar';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { PageHeader } from '@/components/PageHeader';
import { act, DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';
import { otaEnabledActions, otaIdActions } from '@/features/users/userLinks';
import { usePermissions } from '@/hooks/usePermissions';

import { OTAForm } from './OTAForm';

export function OTADetailPage() {
  const { id: idStr } = useParams<{ id: string }>();
  const id = Number(idStr);
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_ota_manage');
  const canViewHistory = hasPerm('console_audit_view');
  // Local tab state — the /otas/:id route has no :tab? segment.
  const [tab, setTab] = useState<'editor' | 'history'>('editor');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const {
    data: ota,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['otas', 'detail', id],
    queryFn: () => otasApi.retrieve(id),
    enabled: Number.isFinite(id),
  });

  const saveMutation = useMutation({
    mutationFn: (body: OTAWrite) => otasApi.update(id, body),
    onSuccess: () => {
      snackbar.show('Saved.', 'success');
      qc.invalidateQueries({ queryKey: ['otas'] });
      void refetch();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => otasApi.remove(id),
    onSuccess: () => {
      snackbar.show('OTA deleted.', 'success');
      qc.invalidateQueries({ queryKey: ['otas'] });
      navigate('/otas');
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error'),
  });

  if (!Number.isFinite(id)) return <Alert severity="warning">Invalid OTA id.</Alert>;
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !ota) return <Alert severity="error">Couldn’t load OTA.</Alert>;

  return (
    <Box>
      <PageHeader
        helpStory="otas-registry"
        title={
          <ValueActions actions={[act.copy(ota.name, 'Copy name')]}>{ota.name}</ValueActions>
        }
        // A node title needs its plain-text twin, or the ⌘K recents entry and
        // the document title both get an empty string. No `section`: this
        // page's tab is local state, not a URL segment, so switching it pushes
        // no history entry to tell apart.
        titleText={ota.name}
        subtitle={
          <DetailSubtitle
            parts={[
              // The id is what every OTA-keyed filter elsewhere takes, so it
              // offers the feature flags scoped to this OTA alongside copy.
              <ValueActions key="id" actions={otaIdActions(ota.id)}>ID {ota.id}</ValueActions>,
              `${ota.domain_count ?? ota.domains?.length ?? 0} domain(s)`,
            ]}
          />
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <ValueActions chip actions={otaEnabledActions(ota.enabled)}>
              <Chip
                size="small"
                variant="outlined"
                color={ota.enabled ? 'success' : 'default'}
                label={ota.enabled ? 'Enabled' : 'Disabled'}
              />
            </ValueActions>
            <PermissionGate perm="console_ota_manage">
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </Button>
            </PermissionGate>
          </Stack>
        }
      />

      {canViewHistory && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v as 'editor' | 'history')}>
            <Tab value="editor" label="Editor" />
            <Tab value="history" label="History" />
          </Tabs>
        </Box>
      )}

      {tab === 'history' ? (
        <HistoryPanel
          contentType="demo.online_travel_agency"
          objectPk={String(ota.id)}
          onRestored={() => void refetch()}
        />
      ) : (
        <OTAForm
          ota={ota}
          canManage={canManage}
          saving={saveMutation.isPending}
          onSubmit={(body) => saveMutation.mutate(body)}
          footer={({ dirty, saving, submit, reset }) => (
            <EditorSaveBar
              dirty={dirty}
              saving={saving}
              canManage={canManage}
              onSave={submit}
              onDiscard={reset}
            />
          )}
        />
      )}

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        <RouterLink to="/otas" style={{ color: '#0a1a3f' }}>
          ← Back to OTAs
        </RouterLink>
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this OTA?"
        message={`"${ota.name}" and its domain rules will be deleted. This cannot be undone.`}
        confirmLabel="Delete OTA"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}
